import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { AlertDialog, Button, Icon, Tooltip } from '@homeexchange/design';
import { addDaysToDate, calculateDaysDifference } from '@homeexchange/shared';

import i18n from '../../i18n';
import Utils from '../../utils/utils';
import Analytics from '../../utils/analytics';

import Conversation from '../../models/Conversation';
import Subscription from '../../models/Subscription';
import Exchange from '../../models/Exchange';
import User from '../../models/User';

import PopupSweet from '../common/PopupSweet';
import FreeTrialGTM from '../../utils/FreeTrialGTM';
import PreApprovedFreeTrialBlocker from './PreApprovedFreeTrialBlocker';

import { bindActionCreators } from 'redux';
import { fetchStayRequestForConversation, preapproveStayRequest } from '../../actions/stayRequest';
import moment from 'moment';

export class ButtonApproval extends React.Component {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        subscription: PropTypes.instanceOf(Subscription),
        approveConversation: PropTypes.func.isRequired,
        user: PropTypes.instanceOf(User),
        nbDaysPreapprovalExpiration: PropTypes.number.isRequired,
        accessToken: PropTypes.string.isRequired,
        actions: PropTypes.shape({
            fetchStayRequestForConversation: PropTypes.func.isRequired,
            preapproveStayRequest: PropTypes.func.isRequired
        })
    };

    constructor(props) {
        super(props);
        this.handleApprobation = this.handleApprobation.bind(this);
        this.approvedOrPayment = this.approvedOrPayment.bind(this);
        this.getExpirationInformations = this.getExpirationInformations.bind(this);
        this.displayExpirationDateFormat = this.displayExpirationDateFormat.bind(this);
        this.renderModalError = this.renderModalError.bind(this);
        this.close = this.close.bind(this);
        this.closeTrialAlertBlocker = this.closeTrialAlertBlocker.bind(this);

        this.state = {
            showPreapprobationModalError: false,
            preapprobationModalErrorProperties: {},
            showPreapprobationModal: false,
            isSending: false,
            isDisabled: false,
            show: false,
            showTrialAlertBlocker: false
        };
    }

    isDisabled() {
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        return !exchangeAsHost.isApprovable() || (exchangeAsGuest && !exchangeAsGuest.isApprovable());
    }

    getTooltipContent() {
        const { conversation } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const exchangeAsHost = conversation.getExchangeAsHost();

        // home not published tooltip as guest
        if (exchangeAsGuest && (!exchangeAsGuest.get('home') || !exchangeAsGuest.get('home').isPublished())) {
            return (
                <>
                    <Icon name="QuestionCircleOutline" /> {i18n.t('exchange:home_offline_guest')}
                </>
            );
        }

        // home not published tooltip has host
        if (exchangeAsHost && (!exchangeAsHost.get('home') || !exchangeAsHost.get('home').isPublished())) {
            return (
                <>
                    <Icon name="QuestionCircleOutline" /> {i18n.t('exchange:home_offline_host')}
                </>
            );
        }

        // missing informaitons tooltip
        if (exchangeAsGuest && !exchangeAsGuest.isFilled()) {
            return (
                <>
                    <Icon name="QuestionCircleOutline" />{' '}
                    <span
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('exchange:missing_informations_guest', {
                                first_name: exchangeAsGuest.get('host').get('first_name'),
                                fields: exchangeAsGuest
                                    .getMissingFields()
                                    .map((field) => i18n.t(`exchange:${field}`))
                                    .join(', ')
                            })
                        }}
                    />
                </>
            );
        }

        // missing informaitons tooltip
        if (exchangeAsHost && !exchangeAsHost.isFilled()) {
            return (
                <>
                    <Icon name="QuestionCircleOutline" />{' '}
                    <span
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('exchange:missing_informations_host', {
                                first_name: exchangeAsHost.get('guest').get('first_name'),
                                fields: exchangeAsHost
                                    .getMissingFields()
                                    .map((field) => i18n.t(`exchange:${field}`))
                                    .join(', ')
                            })
                        }}
                    />
                </>
            );
        }

        if (exchangeAsHost?.hasStartDatePassed() || exchangeAsGuest?.hasStartDatePassed()) {
            return conversation.isMutual()
                ? i18n.t('exchange:approval_dates_in_past_reciprocal')
                : i18n.t('exchange:approval_dates_in_past_guestpoints');
        }

        return null;
    }

    preApprovedConversation() {
        this.setState({
            isDisabled: true,
            isSending: true
        });

        this.props.actions
            .preapproveStayRequest(this.props.conversation.id)
            .then(() => {
                this.props.actions.fetchStayRequestForConversation(this.props.conversation);
                Analytics.trackGTM('MixpanelExchangePreApproved', {
                    userStatus: this.props.user.getStatus()
                });

                Analytics.trackGTM('exchange_status', {
                    event_data: {
                        exchange_status: 'preapproved',
                        collection: this.props.user.isCollection()
                    }
                });
            })
            .catch((error) => {
                this.setState({
                    showPreapprobationModalError: true,
                    preapprobationModalErrorProperties: error
                });
            })
            .finally(() => {
                this.setState({
                    isSending: false,
                    isDisabled: false
                });
            });

        const header = document.querySelector('.exchange-header');
        if (header) {
            header.dispatchEvent(new CustomEvent('loading'));
        }
    }

    handleApprobation(event) {
        event.preventDefault();
        // do nothing if button is disabled
        if (this.isDisabled()) {
            return false;
        }

        const { subscription, user, conversation } = this.props;
        const exchangeEndOn = conversation.getExchange().get('end_on');

        const isTrial = subscription?.get('is_trial') || false;

        if (isTrial) {
            this.setState({ showTrialAlertBlocker: true });
            FreeTrialGTM.trackPreapprovalModalView();
            return;
        }

        if (!user.isSubscriber() && !user.isCollection()) {
            this.showModal(
                true,
                'active-membership-pre-approve',
                'nosubyet',
                i18n.t('messaging:title-modal-pre-approve.nosubyet'),
                `${Utils.getAssetsDomain()}/images/website/messaging/Group134.svg`,
                i18n.t('messaging:content-modal-pre-approve.nosubyet'),
                i18n.t('common:headband.price-increase-information.btn'),
                i18n.t('messaging:link-or-text-modal-pre-approve.nosubyet'),
                i18n.t('url:my-plan.url'),
                true
            );
        } else if (
            !user.isCollection() &&
            subscription &&
            !subscription.get('auto_renew') &&
            moment(exchangeEndOn).isAfter(moment(subscription.get('end_on')), 'day')
        ) {
            this.showModal(
                true,
                'activate-subscription-auto-renew-pre-approve',
                'subexpired',
                i18n.t('messaging:title-modal-pre-approve.subexpired'),
                `${Utils.getAssetsDomain()}/images/website/messaging/Frame.svg`,
                i18n.t('messaging:content-modal-pre-approve.subexpired'),
                i18n.t('messaging:text-button-modal-pre-approve.subexpired'),
                i18n.t('messaging:link-or-text-modal-pre-approve.subexpired', {
                    date: moment(subscription.get('end_on')).format('LL')
                })
            );
        } else {
            this.setState({ showPreapprobationModal: true });
        }
    }

    approvedOrPayment() {
        switch (this.state.swipe) {
            case 'nosubyet':
                document.location.assign(
                    `${document.location.origin}${i18n.t('url:subscription_payment')}?redirectTo=${
                        location.pathname
                    }`
                );
                break;
            case 'subexpired':
                this.preApprovedConversation();
                this.close(false);
                break;
            default:
        }
    }

    close(sendTrackingOnClose = true) {
        // send iterable tracking here
        if (sendTrackingOnClose) {
            const { conversation } = this.props;
            const exchangeAsHost = conversation.getExchangeAsHost();
            const homePictures = exchangeAsHost.get('home').get('images');

            try {
                Analytics.trackIterable('AbandonedPreapprovalAsHost', {
                    email: this.props.user.get('email'),
                    exchangeId: exchangeAsHost.get('id'),
                    exchangeType: exchangeAsHost.getExchangeType(exchangeAsHost.get('type')),
                    exchangeStartOn: moment(exchangeAsHost.get('start_on')).format('DD-MM-YYYY'),
                    exchangeEndOn: moment(exchangeAsHost.get('end_on')).format('DD-MM-YYYY'),
                    exchangeSumGp: exchangeAsHost.getGpTotal(),
                    nightCount: exchangeAsHost.get('night_nb'),
                    guestId: exchangeAsHost.get('guest').get('id'),
                    guestName: exchangeAsHost.get('guest').get('first_name'),
                    hostHomeId: exchangeAsHost.get('home').get('id'),
                    hostHomeName: exchangeAsHost.get('home').get('descriptions')[0].title,
                    hostHomeLocation: exchangeAsHost.get('home').get('address'),
                    hostHomePictureUrl: homePictures.length > 0 ? homePictures[0].cdn_link : null,
                    conversationId: conversation.get('id')
                });
            } catch (error) {
                console.error('Error while tracking Iterable - AbandonedPreapprovalAsHost', error);
            }
        }
        this.setState({ show: false });
    }

    closeTrialAlertBlocker() {
        this.setState({ showTrialAlertBlocker: false });
    }
    showModal(
        show,
        className,
        swipe,
        title,
        image,
        content,
        textButton,
        underTextButton,
        href,
        link = false
    ) {
        this.setState({
            show,
            className,
            swipe,
            title,
            image,
            content,
            textButton,
            underTextButton,
            href,
            link
        });
    }

    displayExpirationDateFormat(date) {
        const options = {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        };

        date.setHours(23, 59, 59);
        const displayExpirationDate = date.toLocaleDateString(i18n.language, options);
        return displayExpirationDate;
    }

    getExpirationInformations() {
        const nbDaysPreapprovalExpiration = this.props.nbDaysPreapprovalExpiration;
        const expirationDate = addDaysToDate(new Date(), nbDaysPreapprovalExpiration);
        const hasPreapprobationExpiration = !this.props.conversation.isMutual();
        const exchangeStartOn = new Date(this.props.conversation.getExchange().get('start_on'));
        const expirationDateIsAfterStartDate = expirationDate > exchangeStartOn;

        return {
            hasPreapprobationExpiration,
            expirationDate: expirationDateIsAfterStartDate
                ? this.displayExpirationDateFormat(exchangeStartOn)
                : this.displayExpirationDateFormat(expirationDate),
            nbDays: expirationDateIsAfterStartDate
                ? calculateDaysDifference(new Date(), new Date(exchangeStartOn))
                : nbDaysPreapprovalExpiration
        };
    }

    render() {
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        const { conversation } = this.props;

        const { hasPreapprobationExpiration, expirationDate, nbDays } = this.getExpirationInformations();
        const {
            show,
            className,
            title,
            image,
            content,
            textButton,
            underTextButton,
            link,
            href,
            showTrialAlertBlocker
        } = this.state;

        if (exchangeAsHost && exchangeAsHost.get('status') == Exchange.STATUS_DEFAULT) {
            return (
                <Fragment>
                    <Tooltip position="top" content={this.getTooltipContent()}>
                        <Button
                            size="medium"
                            isLoading={this.state.isSending}
                            isDisabled={this.isDisabled()}
                            onClick={this.handleApprobation}
                            isFullWidth
                        >
                            {i18n.t('exchange:action_preapprove')}
                        </Button>
                    </Tooltip>
                    <PreApprovedFreeTrialBlocker
                        showTrialAlertBlocker={showTrialAlertBlocker}
                        conversationId={conversation.id}
                        closeTrialAlertBlocker={this.closeTrialAlertBlocker}
                    />
                    <PopupSweet
                        show={show}
                        className={className}
                        title={title}
                        image={image}
                        content={content}
                        textButton={textButton}
                        underTextButton={underTextButton}
                        link={link}
                        href={href}
                        approvedOrPayment={this.approvedOrPayment}
                        closeModal={this.close}
                    />
                    {this.state.showPreapprobationModal && (
                        <AlertDialog
                            isOpen={this.state.showPreapprobationModal}
                            title={i18n.t('exchange:confirm_preapprove_title')}
                            confirmActionText={i18n.t('exchange:confirm_pre_approval')}
                            onConfirmAction={() => {
                                this.preApprovedConversation();
                                this.setState({ showPreapprobationModal: false });
                            }}
                            dismissActionText={i18n.t('common:cancel')}
                            onDismissAction={() => {
                                this.setState({ showPreapprobationModal: false });
                            }}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: i18n.t(
                                        `exchange:${
                                            hasPreapprobationExpiration
                                                ? 'pre_approval_modal_expiration_description'
                                                : 'pre_approval_modal_short_description'
                                        }`,
                                        {
                                            nbDays: nbDays,
                                            date: expirationDate
                                        }
                                    )
                                }}
                            />
                        </AlertDialog>
                    )}

                    {this.state.showPreapprobationModalError && this.renderModalError()}
                </Fragment>
            );
        }
        return false;
    }

    renderModalError() {
        const { preapprobationModalErrorProperties, showPreapprobationModalError } = this.state;

        const errorIsFromLegacy = [
            'error.exchanges.host_already_have_finalized_exchange',
            'error.exchanges.host_already_have_preapproved_exchange'
        ].includes(preapprobationModalErrorProperties.errorData?.message?.errors?.code);

        let alertDialogProps = {
            iconStatus: 'error',
            isOpen: showPreapprobationModalError,
            confirmActionText: i18n.t('common:modal.error-occured.cta'),
            title: i18n.t('common:modal.error-occured.title'),
            children: i18n.t('common:modal.error-occured.content'),
            onConfirmAction: () => {
                this.setState({
                    showPreapprobationModalError: false,
                    preapprobationModalErrorProperties: {}
                });
            }
        };

        if (
            (preapprobationModalErrorProperties?.code === 409 &&
                preapprobationModalErrorProperties.errorData?.message?.errors?.code) ||
            errorIsFromLegacy
        ) {
            if (errorIsFromLegacy) {
                alertDialogProps = {
                    ...alertDialogProps,
                    confirmActionText: i18n.t('common:close'),
                    title:
                        preapprobationModalErrorProperties.errorData?.message?.errors?.code ===
                        'error.exchanges.host_already_have_preapproved_exchange'
                            ? i18n.t('exchange:preapprobation.host_already_preapproved.title')
                            : i18n.t('exchange:preapprobation.host_already_finalized.title'),
                    onConfirmAction: () => {
                        this.setState({
                            showPreapprobationModalError: false,
                            preapprobationModalErrorProperties: {}
                        });
                    },
                    children: (
                        <>
                            {preapprobationModalErrorProperties.errorData?.message?.errors?.code ===
                            'error.exchanges.host_already_have_preapproved_exchange'
                                ? i18n.t('exchange:preapprobation.host_already_preapproved.content')
                                : i18n.t('exchange:preapprobation.host_already_finalized.content')}
                        </>
                    )
                };
            } else {
                const {
                    conversationsId,
                    hostAlreadyHavePreapprovedExchange,
                    hostAlreadyHaveFinalizedExchange
                } = preapprobationModalErrorProperties.errorData.error.exchanges;

                if (hostAlreadyHavePreapprovedExchange || hostAlreadyHaveFinalizedExchange) {
                    alertDialogProps = {
                        ...alertDialogProps,
                        title: hostAlreadyHavePreapprovedExchange
                            ? i18n.t('exchange:preapprobation.host_already_preapproved.title')
                            : i18n.t('exchange:preapprobation.host_already_finalized.title'),
                        dismissActionText: i18n.t('common:close'),
                        children: (
                            <>
                                {hostAlreadyHavePreapprovedExchange
                                    ? i18n.t('exchange:preapprobation.host_already_preapproved.content')
                                    : i18n.t('exchange:preapprobation.host_already_finalized.content')}
                            </>
                        )
                    };

                    if (conversationsId) {
                        alertDialogProps = {
                            ...alertDialogProps,
                            confirmActionText: i18n.t('exchange:preapprobation.view_other_exchange_button'),
                            onConfirmAction: () => {
                                this.setState({
                                    showPreapprobationModalError: false,
                                    preapprobationModalErrorProperties: {}
                                });
                                window.location.href = `${Utils.getUrlLocalePrefix()}/conversations/${
                                    conversationsId[0]
                                }`;
                            }
                        };
                    }
                }
            }
        }
        return <AlertDialog {...alertDialogProps} />;
    }
}

const mapStateToProps = (state) => ({
    nbDaysPreapprovalExpiration: state.stayRequest.config.nbDaysPreapprovalExpiration,
    accessToken: state.auth.accessToken
});

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(
            {
                fetchStayRequestForConversation,
                preapproveStayRequest
            },
            dispatch
        )
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ButtonApproval);
